import { useEffect, useRef } from "react";
import { aboutFeatures } from "@/mocks/bbCoffeeData";

export default function About() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    const els = sectionRef.current?.querySelectorAll(".reveal-on-scroll");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={sectionRef} className="py-20 md:py-28 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center mb-16">
          <div className="flex justify-center">
            <div className="relative w-full max-w-[420px]">
              <div className="absolute -inset-3 border-2 border-primary-300 rounded-2xl" />
              <div className="absolute -inset-1.5 border border-accent-300 rounded-xl" />
              <div className="relative rounded-xl overflow-hidden group">
                <div className="aspect-square w-full">
                  <img
                    alt="BB Coffee food truck at Pusat Perniagaan Bukit Raja, Shah Alam"
                    title="BB Coffee BeanhoodBrew artisan coffee truck Shah Alam"
                    className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                    src="https://static.readdy.ai/image/688e2eae60ac245b19be79aa6846e372/a4bc8535a2e530f9be1fa9d4c01989ec.png"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="text-center lg:text-left">
            <span className="inline-block px-4 py-1.5 mb-4 text-xs font-semibold tracking-widest uppercase text-accent-600 bg-accent-100 rounded-full">
              About Us
            </span>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground-900 mb-6">
              Where Every Cup Tells a <span className="text-primary-600">Story</span>
            </h2>
            <p className="text-foreground-600 leading-relaxed text-base md:text-lg mb-6">
              BB Coffee (BeanhoodBrew) is more than just a coffee truck we&apos;re your friendly neighbourhood coffee stop serving freshly roasted, handcrafted drinks made from our own roasted beans. Conveniently located, affordably priced, and welcoming to coffee lovers of every age we bring the cafe experience to the streets of Shah Alam.
            </p>
            <p className="text-foreground-600 leading-relaxed text-base md:text-lg">
              From our signature Spanish Latte to refreshing fruit sodas, every drink is crafted with passion using beans we roast ourselves. Come find us, say hello, and taste the difference that fresh roasting and heartfelt service make.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {aboutFeatures.map((feature, i) => (
            <div
              key={feature.title}
              className="reveal-on-scroll bg-background-100 hover:bg-background-200/70 rounded-2xl p-6 text-center transition-all duration-500 hover:-translate-y-2 group cursor-default"
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-primary-100 flex items-center justify-center group-hover:bg-primary-500 transition-all duration-500 group-hover:scale-110 group-hover:rounded-full">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className={`${feature.icon} text-2xl text-primary-600 group-hover:text-background-50 transition-colors duration-500`} />
                </div>
              </div>
              <h3 className="font-heading font-semibold text-foreground-900 mb-2 text-sm md:text-base">{feature.title}</h3>
              <p className="text-foreground-500 text-xs md:text-sm leading-relaxed">{feature.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}